import {
  Box,
  Button as ChakraButton,
  Flex,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Text
} from '@chakra-ui/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Banner, FormFeedback } from '@morressier/alembic';
import _ from 'lodash';
import { useFormContext } from 'react-hook-form';
import { FieldLabel } from 'src/components/Field';
import { FormFieldFocusSelect as IFormFieldFocusSelect } from '@publishing-engine/domain/types/article';
import RichTextPreview from '../RichText/RichTextPreview';
import { faChevronDown } from '@fortawesome/pro-regular-svg-icons';

interface Props {
  config: IFormFieldFocusSelect;
  editSelect?: boolean;
  guidelineLink?: string;
}

export const FocusIssueSelect: React.FC<Props> = ({
  config,
  editSelect = false,
  guidelineLink
}) => {
  const {
    params: { validations, labels },
    caseDataFields: { main: fieldPath }
  } = config;

  const {
    formState: { errors },
    setValue,
    watch
  } = useFormContext();

  const error = _.get(errors, fieldPath);
  const selectedValue = watch(fieldPath) as string;

  const isClosedValue =
    !!selectedValue && !validations.options.includes(selectedValue);
  const closedValueName = isClosedValue
    ? labels.options[selectedValue]?.main ?? selectedValue
    : undefined;

  const addGuidelineLink = (text: string) => {
    if (editSelect) {
      return 'You will be editing this submission by changing the special issue or unassigning this submission from a special issue.';
    } else {
      if (text.includes('guidelines')) {
        const replacement = guidelineLink
          ? `<a href="${guidelineLink}" target="_blank" style="color: blue; text-decoration: underline;">guidelines</a>`
          : `<span style="color: gray; cursor: not-allowed;">guidelines</span>`;
        return text.replace('guidelines', replacement);
      }
      return text;
    }
  };

  return (
    <Flex
      gap={4}
      bgColor="surfacePrimaryFill"
      flexDirection="column"
      borderRadius="base"
      width="100%"
    >
      <Text textStyle="headingGroup">
        {!editSelect ? labels.main : `Update ${labels.main}`}
      </Text>
      <Text textStyle="textBody">
        {<RichTextPreview value={addGuidelineLink(labels.subtitle)} />}
      </Text>
      <FieldLabel
        isRequired={validations.required}
        label={editSelect ? labels.main : ''}
        subText=""
      />
      {isClosedValue && (
        <Banner
          heading="For your attention"
          content={
            <Text textStyle={'extBody'}>
              Current Special Issue <strong>{closedValueName}</strong> was closed
              by the Admin. Please update the special issue or select{' '}
              <strong>Clear</strong> from the menu to remove it. To keep the
              existing special issue, click <strong>Cancel</strong>.
            </Text>
          }
          variant="attention"
        />
      )}
      <Menu matchWidth>
        <MenuButton
          as={ChakraButton}
          rightIcon={<FontAwesomeIcon icon={faChevronDown} />}
          textAlign="left"
          justifyContent="space-between"
          fontWeight="normal"
          bg="surfacePrimaryFill"
          border="1px solid"
          borderColor={error ? 'red.500' : 'black'}
          _hover={{ bg: 'surfacePrimaryFill' }}
          _active={{ bg: 'surfacePrimaryFill' }}
          _expanded={{ bg: 'surfacePrimaryFill' }}
          width="100%"
          overflow="hidden"
        >
          <Box
            as="span"
            display="block"
            overflow="hidden"
            textOverflow="ellipsis"
            whiteSpace="nowrap"
          >
            {selectedValue && !isClosedValue
              ? labels.options[selectedValue]?.main
              : `Select this Submission's ${labels.main}`}
          </Box>
        </MenuButton>
        <MenuList maxH="320px" overflowY="auto">
          <MenuItem
            onClick={() => setValue(fieldPath, '', { shouldValidate: true })}
            py={3}
            color="gray.400"
          >
            {`Select this Submission's ${labels.main}`}
          </MenuItem>
          {validations?.options.map(option =>
            option !== '' ? (
              <MenuItem
                key={option}
                onClick={() =>
                  setValue(fieldPath, option, { shouldValidate: true })
                }
                py={3}
                whiteSpace="normal"
              >
                {labels.options[option]?.main}
              </MenuItem>
            ) : null
          )}
          {isClosedValue && (
            <MenuItem
              onClick={() => setValue(fieldPath, '', { shouldValidate: true })}
              py={3}
              color="red.500"
            >
              Clear
            </MenuItem>
          )}
        </MenuList>
      </Menu>
      {!!error?.message && (
        <FormFeedback variant="error">
          Please select {labels.main} to proceed.
        </FormFeedback>
      )}
    </Flex>
  );
};
